import { useMemo } from "react";
import { StatCardResultDto } from "api/interfaces/planInterface.interface";
import { formatNumber } from "utils/functions";
import * as s from "./BreakdownStyled";

export const CHANNEL_BREAKDOWN_TITLE = "Average daily channels calculation";
export const CHANNEL_BREAKDOWN_SUBTITLE =
  "Step-by-step math behind how average daily channels is calculated from this month's live usage.";

type Props = {
  summary: StatCardResultDto;
  sectionTitle?: string;
};

export function useChannelBreakdown(summary: StatCardResultDto) {
  const sumChannel = summary.activeChannels?.sumChannel ?? 0;
  const channelPrice = summary.activeChannels?.channelPrice ?? 0;
  const cycleDays = summary.cycleDays ?? 30;

  return useMemo(() => {
    const totalChannelDays = sumChannel;
    const avgDailyChannels = sumChannel / cycleDays;
    const channelCost = avgDailyChannels * channelPrice;

    return {
      cycleDays,
      channelPrice,
      totalChannelDays,
      avgDailyChannels,
      channelCost,
    };
  }, [sumChannel, cycleDays, channelPrice]);
}

export default function ChannelBreakdownContent({
  summary,
  sectionTitle = "Channel-days calculation",
}: Props): JSX.Element {
  const {
    cycleDays,
    channelPrice,
    totalChannelDays,
    avgDailyChannels,
    channelCost,
  } = useChannelBreakdown(summary);

  return (
    <s.Wrapper>
      <s.Section>
        <div className="section-title">{sectionTitle}</div>
        <div className="section-desc">
          Channel-days are the building block of prorated billing. For every
          period in the month where the channel count stays the same, multiply
          channels × days, then add the periods together. Days after today are
          projected at the current channel count so the full billing period is
          covered.
        </div>
      </s.Section>

      <s.Box>
        <div className="box-title">
          Formula: Channel-days = Σ (channels × days at that level)
        </div>
        <s.Row>
          <span className="label">Total channel-days</span>
          <span className="value">
            {formatNumber(totalChannelDays)} (Σ channels × days)
          </span>
        </s.Row>
        <s.Row>
          <span className="label">Days in month</span>
          <span className="value">{cycleDays}</span>
        </s.Row>
        <s.Divider />
        <s.Row className="highlight">
          <span className="label">Avg daily channels</span>
          <span className="value">
            {formatNumber(totalChannelDays)} ÷ {cycleDays} ={" "}
            {avgDailyChannels.toFixed(2)}
          </span>
        </s.Row>
        <s.Divider />
        <s.Row>
          <span className="label">Channel cost</span>
          <span className="value">
            {avgDailyChannels.toFixed(2)} × ${formatNumber(channelPrice)} = $
            {formatNumber(channelCost)}
          </span>
        </s.Row>
      </s.Box>
    </s.Wrapper>
  );
}
